import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { xml2js } from "../../json/xml2js";
import queryString from "query-string";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import moment from "moment";
import ClientFormHeader from "../common/ClientFormHeader";
import FormButtonBar from "../common/FormButtonBar";
import Signature from "../common/Signature";
import Comment from "../common/Comment";
import BackIcon from "../common/BackIcon";
import ShareModal from "../common/ShareModal";
import {
  Input,
  Select,
  Checkbox,
  RadioButton,
  FieldTable,
} from "../common/ControlBundle";
import {
  getCareTeams,
  getConfig,
  getSubmission,
  saveForm,
  cloneForm,
  deleteFormSubmission,
  validateForm,
  handleFormChange,
  compareForms,
  getFormsySubmissionsForClient,
  sendShareEmail,
  getClientData,
  handleDeleteFieldTableRow,
  handleFieldTableChange,
  handleCopyUrl,
} from "../../scripts/formScripts";
import {
  getFieldName,
  getFieldText,
  getFieldValue,
  getFieldDate,
  ArrayCheck,
  stripHtml,
} from "../../scripts/helperScripts";
import { getSchema } from "./joi/validation";
import { getSignedEncounters } from "../services/soar";
import generalOptionSets from "../../options/general.json";

function OHTFCMonthlyVisitationMarionv2() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialProgram = location.state?.selectedProgram;

  //Get route and query string params
  const { clientid, formid, submissionid } = useParams();
  const qs = queryString.parse(location.search);
  console.log("Params", clientid, formid, submissionid);
  console.log("QS:", qs);

  //Get logged in user info
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  //The complete Form Submission object with form data
  const [formsyDetails, setFormsyDetails] = useState({});
  const [formsyConfig, setFormsyConfig] = useState({});
  const [loadingConfig, setLoadingConfig] = useState(false);
  //The form data and previous forms
  const [data, setData] = useState({});
  const [allFormSubmissions, setAllFormSubmissions] = useState([]);
  //Touched fields
  const [touched, setTouched] = useState({});
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  //Errors
  const [errors, setErrors] = useState({});
  //Client, Program and CareTeam Info
  const [clientData, setClientData] = useState({});
  const [careTeams, setCareTeams] = useState([]);

  //Signatures State Property
  const [signatures, setSignatures] = useState([]);
  //Show Signature Modal
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  //Comments state Property
  const [comments, setComments] = useState();
  //Show Comments Modal
  const [showComment, setShowComment] = useState(false);
  //Show Share Modal
  const [showShare, setShowShare] = useState(false);
  //Show Form Level (not field specific) Comments
  const [showFormComment, setShowFormComment] = useState(false);

  const [doCompare, setDoCompare] = useState(false);
  const [timeoutId, setTimeoutId] = useState();

  const pdfRef = useRef(true);
  const isMountedRef = useRef(true);

  //Get validation schema
  const schema = getSchema(formid);
  console.log("Schema Config: ", schema);

  const selectedProgramId = useMemo(() => {
    return data.SelectedProgram?.["@value"];
  }, [data.SelectedProgram]);

  const formDisabled = useMemo(() => {
    return formsyDetails.Active === "0" || formsyDetails.FinalSigned === "1";
  }, [formsyDetails]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      pdfRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (
      !initialProgram?.ccx_client_programid ||
      !data.SelectedProgram ||
      data.SelectedProgram["@value"]
    ) {
      return;
    }

    const obj = { ...data };
    obj.SelectedProgram["@value"] = initialProgram.ccx_client_programid;
    setData(obj);

    if (formsyDetails.ClientProgramId === initialProgram.ccx_client_programid) {
      return;
    }

    const details = { ...formsyDetails };
    details.ClientProgramId = initialProgram.ccx_client_programid;
    details.ClientProgramName = getFieldName(initialProgram.ccx_programunit);
    setFormsyDetails(details);
  }, [initialProgram, selectedProgramId, data, formsyDetails]);

  //Get Client Info
  useEffect(() => {
    if (!formid || !clientid) {
      return;
    }

    const loadClientInfo = async () => {
      const response = await Promise.all([
        getFormsySubmissionsForClient(formid, clientid),
        getCareTeams(clientid),
      ]);

      if (!isMountedRef.current || !response) {
        return;
      }

      setAllFormSubmissions(response[0]);
      setCareTeams(response[1]);
    };

    loadClientInfo();

    if (submissionid !== undefined) {
      const loadSubmission = async () => {
        const response = await getSubmission(submissionid);

        if (!isMountedRef.current || !response) {
          return;
        }

        setData(response.Data);
        setFormsyDetails(response.FormsyDetails);

        if (response.Comments) {
          setComments(response.Comments);
        }

        if (response.Signatures) {
          setSignatures(response.Signatures);
        }
      };

      loadSubmission();
    }
  }, [formid, clientid, submissionid]);

  useEffect(() => {
    if (
      !formid ||
      !clientid ||
      Object.keys(formsyConfig).length > 0 ||
      loadingConfig ||
      !safyInfo?.user
    ) {
      return;
    }

    const loadConfig = async () => {
      setLoadingConfig(true);
      const response = await getConfig(formid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setFormsyConfig(response);
      const clientDataResponse = await getClientData(
        clientid,
        submissionid !== undefined,
        `${response.FormName}${
          response.VersionIntId > 1 ? `v${response.VersionIntId}` : ""
        }`
      );

      if (!isMountedRef.current || !clientDataResponse) {
        return;
      }

      setClientData(clientDataResponse);

      if (!submissionid) {
        //Set Formsy Details
        const newForm = {
          SubmissionId: "",
          FormId: response.FormId,
          FormVersionId: formid,
          ContactId: clientid,
          ContactName: clientDataResponse.client.fullName,
          ClientProgramId: "",
          ClientProgramName: "",
          TertiaryObjectId: "",
          TertiaryObjectName: "",
          FormResults: {
            results: {},
          },

          Comments: "",
          Signatures: "",
          CreatedOn: "",
          CreatedById: safyInfo.user.azureId,
          CreatedByName: safyInfo.user.displayName,
          ModifiedById: safyInfo.user.azureId,
          ModifiedByName: safyInfo.user.displayName,
          FinalSigned: "0",
          FinalSignedDate: "",
          Active: "1",
        };
        setFormsyDetails(newForm);
        setData(response.FormConfiguration[0].config[0]);
        setLoadingConfig(false);

        console.log("New Form:", newForm);
        console.log(
          "loadConfig setData:",
          response.FormConfiguration[0].config[0]
        );
      }
    };

    loadConfig();
  }, [
    loadingConfig,
    formid,
    clientid,
    safyInfo?.user,
    formsyConfig,
    submissionid,
  ]);

  useEffect(() => {
    if (!submissionid || !!formsyDetails.SubmissionId) {
      return;
    }

    const obj = { ...formsyDetails };
    obj.SubmissionId = submissionid;
    setFormsyDetails(obj);
  }, [submissionid, formsyDetails]);

  const helperConfiguration = useMemo(() => {
    try {
      return JSON.parse(formsyConfig.HelperConfiguration[0]);
    } catch {
      const defaultConfig = {
        buttonBarConfig: {
          sign: {
            display: false,
            initialSignatureType: "full",
          },
          clone: {
            display: false,
          },
          delete: {
            display: false,
          },
          share: {
            display: false,
          },
          comment: {
            display: false,
          },
        },
        signatureType: "full",
        finalSignType: "Provider",
      };

      return defaultConfig;
    }
  }, [formsyConfig.HelperConfiguration]);

  const getSelectedProgramName = (dataObj, cData) => {
    if (!dataObj.SelectedProgram || !cData.programs) {
      return "";
    }

    let name = getFieldName(dataObj.SelectedProgram);
    if (name) {
      return name;
    } else {
      const selectedProgram = cData.programs.filter(
        (p) => p.ccx_client_programid === getFieldValue(dataObj.SelectedProgram)
      )[0];

      if (selectedProgram) {
        name = getFieldName(selectedProgram.ccx_programunit);
        const programDates = ` (${getFieldDate(
          selectedProgram.ccx_admitdate
        )} - ${
          selectedProgram.ccx_dischargedate
            ? getFieldDate(selectedProgram.ccx_dischargedate)
            : "no date"
        })`;
        return name + programDates;
      } else {
        return "";
      }
    }
  };

  //Form validation
  const validate = () => {
    const results = validateForm(data, schema);
    setErrors(results);
    return results;
  };

  const handleChange = ({ currentTarget: input }) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const result = handleFormChange(input, data, schema, errors);
    setData(result.Data);
    setErrors(result.Errors);

    if (!submissionid) {
      return result.Data;
    }

    const touchedObj = { ...touched, [input.name]: true };
    const touchedFields = Object.keys(touchedObj).filter(
      (field) => touchedObj[field]
    );

    if (touchedFields.length >= 5) {
      if (isSaveInProgress) {
        const toId = setTimeout(() => {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }, 20000);

        setTimeoutId(toId);
      } else {
        clearTimeout(timeoutId);
        handleSubmit(result.Data, null, null, null, true);
        touchedFields.forEach((field) => (touchedObj[field] = false));
      }
    } else {
      const toId = setTimeout(() => {
        if (isSaveInProgress) {
          const toId2 = setTimeout(() => {
            touchedFields.forEach((field) => (touchedObj[field] = false));
            handleSubmit(result.Data, null, null, null, true);
          }, 20000);

          setTimeoutId(toId2);
        } else {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }
      }, 20000);

      setTimeoutId(toId);
    }

    setTouched(touchedObj);
    return result.Data;
  };

  const handleRowChange = (e, index, fieldname) => {
    const rowObj = handleFieldTableChange(data, e, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleDeleteRow = (index, fieldname) => {
    const rowObj = handleDeleteFieldTableRow(data, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  ////SAVE
  const handleSubmit = async (
    dataOverride,
    formsyDetailsOverride,
    commentsOverride,
    signaturesOverride,
    autoSave = false
  ) => {
    if (isSaveInProgress) {
      return;
    }

    const dataObj = dataOverride || data;
    const formsyDetailsObj = formsyDetailsOverride || formsyDetails;
    const commentsObj =
      commentsOverride === undefined ? undefined : commentsOverride || comments;
    const signaturesObj = signaturesOverride || signatures;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    setTouched({});
    const errs = { ...errors };

    if (!dataObj.SelectedProgram?.["@value"]) {
      errs.SelectedProgram = '"Selected Program" is required';
      setErrors(errs);
      return;
    }

    delete errs.SelectedProgram;
    setErrors(errs);
    setIsSaveInProgress(true);

    try {
      const submissionResponse = await saveForm(
        dataObj,
        formsyDetailsObj,
        commentsObj,
        signaturesObj,
        navigate,
        location,
        autoSave
      );

      if (!isMountedRef.current || !submissionResponse?.Id) {
        return;
      }

      const newDetails = { ...formsyDetailsObj };
      newDetails.ModifiedOn = submissionResponse.LastModifiedOn;
      newDetails.ModifiedById = safyInfo.user.azureId;
      newDetails.ModifiedByName = safyInfo.user.displayName;

      if (newDetails.SubmissionId === "") {
        newDetails.SubmissionId = submissionResponse?.Id;
      }

      setFormsyDetails(newDetails);
    } finally {
      setIsSaveInProgress(false);
    }
  };

  ////CLONE
  const handleOnClone = (event) => {
    event.preventDefault();
    cloneForm(
      data,
      formsyDetails,
      comments,
      signatures,
      navigate,
      location,
      formsyConfig
    );
    setSignatures([]);
  };

  ////DELETE
  const handleDelete = (e) => {
    e.preventDefault();
    if (submissionid) {
      deleteFormSubmission(
        data,
        formsyDetails,
        comments,
        signatures,
        navigate,
        location
      );
    } else {
      alert("The form could not be deleted because it has not been saved");
    }
  };

  ////SHARE
  const toggleShareModal = () => {
    setShowShare(!showShare);
  };

  const handleShareSend = (recipientList, shareComments) => {
    sendShareEmail(
      recipientList,
      shareComments,
      location.pathname,
      data,
      formsyDetails,
      comments,
      signatures,
      formsyConfig.FormDescription
    );
  };

  ////COMMENTS FUNCTIONALITY
  const [commentToEdit, setCommentToEdit] = useState();

  const toggleCommentModal = (editComment) => {
    if (!showComment && editComment) setCommentToEdit(editComment);
    setShowComment(!showComment);
  };

  const handleComments = ({ action, fieldName, comment, acknowledge }) => {
    if (action === "ADD") {
      setShowComment(true);
    } else if (comment !== undefined) {
      if (action === "EDIT") {
        toggleCommentModal(comment);
      } else if (action === "DELETE") {
        handleDeleteComment(comment);
      } else if (action === "ACKNOWLEDGE" && acknowledge !== undefined) {
        handleAcknowledgeComment(comment, acknowledge);
      }
    } else if (action === "GET") {
      return parsedComments.filter(
        (comment) => comment.field._text === fieldName
      );
    }
  };

  const commentFields = () => {
    let commentOptions = [];
    commentOptions.push({ _id: "EntireForm", name: "Entire Form----" });
    for (const property in data) {
      if (
        data[property]["@type"] !== "section" &&
        data[property]["@type"] !== "label" &&
        data[property]["@type"] !== "User" &&
        data[property]["@type"] !== "hidden"
      ) {
        const option = {
          _id: property,
          name:
            data[property]["@label"] !== undefined &&
            data[property]["@label"] !== ""
              ? data[property]["@label"]
              : property,
        };
        commentOptions.push(option);
      }
    }
    return commentOptions;
  };

  const parsedComments = useMemo(() => {
    if (!comments) {
      return [];
    }

    const commentObj = xml2js(`<comments type="array">${comments}</comments>`, {
      compact: true,
      spaces: 4,
    });

    return ArrayCheck(commentObj.comments.comment);
  }, [comments]);

  const onCommentSave = (comment, commentToEdit) => {
    let formsyComments;
    if (commentToEdit) {
      const commentXml = `<comment><createdby>${
        commentToEdit.createdby._text
      }</createdby><datetime>${commentToEdit.datetime._text}</datetime><field>${
        commentToEdit.field._text
      }</field><comment>${commentToEdit.comment._text}</comment>${
        commentToEdit.acknowledgedby
          ? `<acknowledgedby>${commentToEdit.acknowledgedby._text}</acknowledgedby><acknowledgedon>${commentToEdit.acknowledgedon._text}</acknowledgedon>`
          : ""
      }</comment>`;
      console.log("CurrentCommentsXML: ", comments);
      console.log("CommentToReplace: ", commentXml);
      console.log("CommentToEdit: ", commentToEdit);
      console.log("CommentToEditXML: ", comment);

      formsyComments =
        comments !== undefined
          ? comments.replace(commentXml, comment)
          : comment;
      setComments(formsyComments);
      setCommentToEdit(undefined);
    } else {
      formsyComments = comments !== undefined ? comments + comment : comment;
      setComments(formsyComments);
    }

    setShowComment(false);
    handleSubmit(null, null, formsyComments, null);
  };

  const handleDeleteComment = (comment) => {
    const commentXml = `<comment><createdby>${
      safyInfo.user.displayName
    }</createdby><datetime>${comment.datetime._text}</datetime><field>${
      comment.field._text
    }</field><comment>${comment.comment._text}</comment>${
      comment.acknowledgedby
        ? `<acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon>`
        : ""
    }</comment>`;

    let formsyComments = comments.replace(commentXml, "");
    if (formsyComments === "") formsyComments = undefined;
    setComments(formsyComments);
    handleSubmit(null, null, formsyComments, null);
  };

  const toggleCommentToEdit = (comment) => {
    setCommentToEdit(comment);
    setShowComment(true);
  };

  const handleAcknowledgeComment = (comment, acknowledge) => {
    if (acknowledge) {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const newCommentXML = `<comment><createdby>${
        comment.createdby._text
      }</createdby><datetime>${comment.datetime._text}</datetime><field>${
        comment.field._text
      }</field><comment>${comment.comment._text}</comment><acknowledgedby>${
        safyInfo.user.displayName
      }</acknowledgedby><acknowledgedon>${moment().format(
        "L"
      )} ${moment().format("LT")}</acknowledgedon></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    } else {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment><acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon></comment>`;
      const newCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    }
  };

  const handleCommentNavigation = (fieldName) => {
    const element = document.getElementById(fieldName);
    if (element.type === "checkbox")
      scrollTo(
        document.getElementById(fieldName).parentElement.parentElement
          .parentElement.offsetTop - 50,
        () => {
          document.getElementById(fieldName).focus();
        }
      );
    else
      scrollTo(document.getElementById(fieldName).offsetTop - 50, () => {
        document.getElementById(fieldName).focus();
      });
  };

  const renderComments = () => {
    return parsedComments
      .filter(
        (comment) =>
          comment.field._text === "EntireForm" ||
          comment.field._text === "Entire Form----"
      )
      .map((comment, index) => (
        <div className="mt-2" key={`form-comment-${index}`}>
          {showFormComment && (
            <div
              className={`${
                comment.acknowledgedby ? "bg-safy3-50" : "bg-warning"
              } p-2 m-2 rounded d-print-none`}
              title={
                comment.acknowledgedby
                  ? `Acknowledged by: ${comment.acknowledgedby._text} ${comment.acknowledgedon._text}`
                  : ""
              }
            >
              <span className="h6">
                {comment.createdby._text ===
                  JSON.parse(localStorage.getItem("safyInfo")).user
                    .displayName && (
                  <>
                    <i
                      title="Delete Comment"
                      className="far fa-trash-alt text-white me-2"
                      onClick={() => {
                        confirmAlert({
                          title: "Delete Comment",
                          message:
                            "Are you sure you want to delete this comment?",
                          buttons: [
                            {
                              label: "Yes",
                              onClick: () => {
                                handleDeleteComment(comment);
                              },
                            },
                            {
                              label: "No",
                              onClick: () => {
                                /* do nothing */
                              },
                            },
                          ],
                        });
                      }}
                    ></i>
                    {!comment.acknowledgedby && (
                      <i
                        title="Edit Comment"
                        className="fas fa-pen text-white me-2"
                        onClick={(e) => {
                          toggleCommentToEdit(comment);
                        }}
                      ></i>
                    )}
                  </>
                )}
                {comment.acknowledgedby && (
                  <i
                    className="far fa-check-square text-white me-2"
                    onClick={() => {
                      if (
                        comment.acknowledgedby._text ===
                        JSON.parse(localStorage.getItem("safyInfo")).user
                          .displayName
                      )
                        handleAcknowledgeComment(comment, false);
                    }}
                  ></i>
                )}
                {!comment.acknowledgedby && (
                  <i
                    className="far fa-square text-white me-2"
                    onClick={() => {
                      handleAcknowledgeComment(comment, true);
                    }}
                    title="Acknowledge Comment"
                  ></i>
                )}
                <br />
                {`${comment.createdby._text} - ${comment.datetime._text}`}
              </span>
              <br />
              Regarding:{" "}
              {comment.field._text !== "EntireForm" &&
              comment.field._text !== "Entire Form----" ? (
                <button
                  className="btn btn-link"
                  onClick={() => {
                    setTimeout(() => {
                      handleCommentNavigation(comment.field._text);
                    }, 100);
                  }}
                >
                  {comment.field._text}
                </button>
              ) : (
                <>{comment.field._text}</>
              )}
              <br />
              {comment.comment._text}
            </div>
          )}
        </div>
      ));
  };

  function scrollTo(offset, callback) {
    const fixedOffset = offset.toFixed();
    const onScroll = function () {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener("scroll", onScroll);
        callback();
      }
    };

    window.addEventListener("scroll", onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });
  }

  //SIGNATURE FUNCTIONALITY
  const toggleSignatureModal = (e) => {
    e.preventDefault();
    if (!isSaveInProgress) setShowSignatureModal(true);
  };

  const saveSignature = async (signatureData) => {
    console.log("Signature Data:", signatureData);

    let sigArray = [];
    if (Array.isArray(signatureData)) {
      sigArray = signatureData;
    } else {
      sigArray.push(signatureData);
    }
    setSignatures(sigArray);

    const formsyDetailsObj = { ...formsyDetails };

    if (formsyDetailsObj.FinalSigned !== "1" && checkFinalSigned(sigArray)) {
      console.log("Final Signed");
      formsyDetailsObj.FinalSigned = "1";

      if (
        formsyDetailsObj.FinalSignedDate === "" ||
        formsyDetailsObj.FinalSignedDate === "1900-01-01T00:00:00"
      ) {
        formsyDetailsObj.FinalSignedDate = new Date();
      }

      setFormsyDetails(formsyDetailsObj);
      setComments(undefined);
    }

    handleSubmit(
      null,
      formsyDetailsObj,
      formsyDetailsObj.FinalSigned === "1" ? undefined : null,
      sigArray
    );
  };

  const checkFinalSigned = (sigArray) => {
    const requiredSignatureTypes = helperConfiguration.finalSignType.split(",");

    const capturedSignatureTypes = sigArray.map((sig) => sig.signatureType);
    console.log("requiredSignatureTypes: ", requiredSignatureTypes);
    console.log("capturedSignatureTypes: ", capturedSignatureTypes);

    const missingSigTypes = requiredSignatureTypes.filter(
      (type) => !capturedSignatureTypes.includes(type)
    );

    return missingSigTypes.length === 0;
  };

  //COMPARE
  const compare = (field) => {
    if (doCompare)
      return compareForms(field, data, allFormSubmissions, formsyDetails);
  };

  const toggleCompare = () => {
    setDoCompare(!doCompare);
  };

  //CUSTOM
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [encounters, setEncounters] = useState([]);

  useEffect(() => {
    if (formDisabled || loading || loaded) {
      return;
    }

    const loadEncounters = async () => {
      setLoading(true);
      const response = await getSignedEncounters(clientid);

      if (!isMountedRef.current) {
        return;
      }

      setEncounters(response || []);
      setLoaded(true);
      setLoading(false);
    };

    loadEncounters();
  }, [clientid, formDisabled, loading, loaded]);

  const convertEncounters = useCallback(() => {
    setData((d) => {
      const obj = { ...d };
      const selectedProgram = getFieldValue(obj.SelectedProgram)
        ? getFieldValue(obj.SelectedProgram)
        : null;
      const startDate = getFieldValue(obj.StartDate)
        ? moment(getFieldValue(obj.StartDate))
        : null;
      const endDate = getFieldValue(obj.EndDate)
        ? moment(getFieldValue(obj.EndDate))
        : null;

      console.group("Process Records");
      console.log("Selected Program: ", selectedProgram);
      console.log("Start Date: ", startDate);
      console.log("End Date: ", endDate);
      console.log("Encounters: ", encounters);
      console.groupEnd();

      if (!selectedProgram || !startDate || !endDate) {
        return obj;
      }

      const monthlyEncounters = encounters.filter((encounter) => {
        const encounterStartDate = moment(
          getFieldText(encounter.ccx_starttime)
        ).startOf("day");

        return (
          getFieldText(encounter.ccx_clientprogram) ===
            getFieldValue(obj.SelectedProgram) &&
          encounterStartDate.isSameOrAfter(startDate) &&
          encounterStartDate.isSameOrBefore(endDate)
        );
      });

      if (monthlyEncounters.length > 0) {
        console.log("Monthly Encounters: ", monthlyEncounters);
        const existingContactDetails =
          obj.ContactDetails["@value"] !== undefined &&
          obj.ContactDetails["@value"] !== ""
            ? JSON.parse(obj.ContactDetails["@value"]).filter((cd) => {
                const encounterStartDate = moment(
                  getFieldText(cd.FullStartDate)
                ).startOf("day");

                return (
                  encounterStartDate.isSameOrAfter(startDate) &&
                  encounterStartDate.isSameOrBefore(endDate)
                );
              })
            : [];

        const existingRecordIds = existingContactDetails.map(
          (service) => service.RecordId
        );

        const serviceDetails = monthlyEncounters
          .filter(
            (encounter) =>
              !existingRecordIds.includes(encounter.ccx_encounterid)
          )
          .map((encounter) => {
            const startTime = moment(getFieldText(encounter.ccx_starttime));
            const endTime = moment(getFieldText(encounter.ccx_endtime));

            return {
              IsEditable: true,
              RecordId: encounter.ccx_encounterid,
              VisitDate: startTime.format(moment.HTML5_FMT.DATE),
              VisitBegin: startTime.format("LT"),
              VisitEnd: endTime.format("LT"),
              VisitDuration: endTime.diff(startTime, "minutes"),
              VisitParties: getFieldName(encounter.ccx_recipientcode),
              FullStartDate: encounter.ccx_starttime,
              FullEndDate: encounter.ccx_endtime,
            };
          });

        console.log(serviceDetails);
        let allContactDetails = existingContactDetails
          .concat(serviceDetails)
          .sort((a, b) => {
            if (!a.FullStartDate || !b.FullStartDate) {
              return -1;
            }

            const aDate = moment(a.FullStartDate);
            const bDate = moment(b.FullStartDate);

            return aDate.isBefore(bDate) ? -1 : bDate.isBefore(aDate) ? 1 : 0;
          });

        if (allContactDetails.count === 0)
          allContactDetails = [{ IsEditable: true }];

        console.log("Service Details: ", allContactDetails);
        obj.ContactDetails["@value"] = JSON.stringify(allContactDetails);
      }

      return obj;
    });
  }, [encounters]);

  const handleReportMonthChange = ({ currentTarget: input }) => {
    handleChange({ currentTarget: input });

    if (!input.value) {
      return;
    }

    setData((d) => {
      const obj = { ...d };
      const startOfMonth = moment();
      const endOfMonth = moment();

      if (Number(getFieldValue(obj.ReportMonth) > startOfMonth.month() + 1)) {
        startOfMonth.subtract(1, "year");
        endOfMonth.subtract(1, "year");
      }

      startOfMonth.month(obj.ReportMonth["@value"] - 1).startOf("month");
      endOfMonth.month(obj.ReportMonth["@value"] - 1).endOf("month");
      obj.StartDate["@value"] = startOfMonth.format(moment.HTML5_FMT.DATE);
      obj.EndDate["@value"] = endOfMonth.format(moment.HTML5_FMT.DATE);
      return obj;
    });
    convertEncounters();
  };

  const handleReportDateChange = ({ currentTarget: input }) => {
    handleChange({ currentTarget: input });

    if (!input.value) {
      return;
    }

    convertEncounters();
  };

  const checkChanges = (obj) => {
    return (
      obj.ChangesMarital["@value"] === "true" ||
      obj.ChangesHealth["@value"] === "true" ||
      obj.ChangesPlacement["@value"] === "true" ||
      obj.ChangesBirth["@value"] === "true" ||
      obj.ChangesDeath["@value"] === "true" ||
      obj.ChangesCrime["@value"] === "true" ||
      obj.ChangesMembers["@value"] === "true" ||
      obj.ChangesRelocation["@value"] === "true" ||
      obj.ChangesActivities["@value"] === "true" ||
      obj.ChangesEmployment["@value"] === "true"
    );
  };

  ////UI
  return (
    <>
      <div className="mb-2">
        <BackIcon />
      </div>
      <div className={!submissionid ? "watermarked" : ""} ref={pdfRef}>
        <ClientFormHeader
          formTitle={formsyConfig.FormDescription}
          clientData={clientData.client}
          formsyDetails={formsyDetails}
        />
        <FormButtonBar
          config={helperConfiguration.buttonBarConfig}
          onSave={handleSubmit}
          onClone={handleOnClone}
          onDelete={handleDelete}
          onSignClicked={toggleSignatureModal}
          onComment={toggleCommentModal}
          onCompare={toggleCompare}
          onShare={toggleShareModal}
          signed={signatures.length > 0}
          finalSigned={formsyDetails.FinalSigned === "1"}
          validate={validate}
          pdfRef={pdfRef}
          filename={`${formsyConfig.FormDescription}`}
          newestVersion={
            formsyConfig.VersionIntId === formsyConfig.LatestVersionIntId
          }
        />
        <div className="py-2 border-bottom border-dark">
          <b>Selected Client Program:</b>{" "}
          {getSelectedProgramName(data, clientData)}
        </div>
        {renderComments()}
        {parsedComments.filter(
          (comment) =>
            comment.field._text === "EntireForm" ||
            comment.field._text === "Entire Form----"
        ).length > 0 && (
          <i
            className="fas fa-comment-alt text-warning float-end mb-2 ms-2"
            onClick={() => setShowFormComment(!showFormComment)}
          ></i>
        )}
        <div className="mt-4">
          {data && Object.keys(data).length > 0 && (
            <>
              <Input
                name="CurrentFacility"
                label={data.CurrentFacility["@label"]}
                error={errors.CurrentFacility}
                value={getFieldValue(data.CurrentFacility)}
                onChange={handleChange}
                identical={compare("CurrentFacility")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="PrimaryCaregiver"
                label={data.PrimaryCaregiver["@label"]}
                error={errors.PrimaryCaregiver}
                value={getFieldValue(data.PrimaryCaregiver)}
                onChange={handleChange}
                identical={compare("PrimaryCaregiver")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="SecondaryCaregiver"
                label={data.SecondaryCaregiver["@label"]}
                error={errors.SecondaryCaregiver}
                value={getFieldValue(data.SecondaryCaregiver)}
                onChange={handleChange}
                identical={compare("SecondaryCaregiver")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <div className="row">
                <div className="col-6">
                  <Select
                    name="ReportMonth"
                    label="Report Month:"
                    error={errors.ReportMonth}
                    value={data.ReportMonth["@value"]}
                    options={generalOptionSets.month}
                    onChange={handleReportMonthChange}
                    identical={compare("ReportMonth")}
                    commentsHandler={handleComments}
                    disabled={formDisabled || !loaded}
                    divClassName="w-50"
                  />
                </div>
                <div className="col">
                  <Input
                    name="StartDate"
                    label={data.StartDate["@label"]}
                    error={errors.StartDate}
                    value={getFieldValue(data.StartDate)}
                    onChange={(e) => {
                      handleReportDateChange(e, null, data.EndDate["@value"]);
                    }}
                    commentsHandler={handleComments}
                    disabled={formDisabled || !loaded}
                    type="date"
                  />
                </div>
                <div className="col">
                  <Input
                    name="EndDate"
                    label={data.EndDate["@label"]}
                    error={errors.EndDate}
                    value={getFieldValue(data.EndDate)}
                    onChange={(e) => {
                      handleReportDateChange(e, data.StartDate["@value"], null);
                    }}
                    commentsHandler={handleComments}
                    disabled={formDisabled || !loaded}
                    type="date"
                  />
                </div>
              </div>
              <FieldTable
                name="ContactDetails"
                commentsHandler={handleComments}
              >
                <div className="clearfix">
                  <div className="float-start">
                    {data.ContactDetails["@label"]}
                  </div>
                  <div className="float-end">
                    <button
                      className="btn btn-link"
                      title="Open Encounter"
                      onClick={() => {
                        const encounterEnvironment =
                          process.env.REACT_APP_ENVIRONMENT === "Production"
                            ? "ccplive-cp"
                            : "d365base-cp";

                        handleCopyUrl(location.pathname).then(() => {
                          window.open(
                            `https://${encounterEnvironment}.safy.org/dist/EncounterPortal/index.html?contactid=%7B${clientid}%7D`
                          );
                        });
                      }}
                    >
                      {data.EncounterLinkLabel["@label"]}
                    </button>
                  </div>
                </div>
                <div className="container mb-2">
                  {data.ContactDetails["@value"]
                    ? JSON.parse(getFieldValue(data.ContactDetails)).map(
                        (item, index) => (
                          <div
                            id={`ContactDetails.${item._id}`}
                            key={`ContactDetails.${item._id}`}
                            className="p-2 border mb-2"
                          >
                            <Input
                              name="VisitDate"
                              label={
                                <div className="d-flex">
                                  {data.VisitDateLabel["@label"]}
                                  <i
                                    className="far fa-trash-alt ms-2 mt-1 d-print-none"
                                    onClick={() => {
                                      handleDeleteRow(index, "ContactDetails");
                                    }}
                                  ></i>
                                </div>
                              }
                              value={item.VisitDate}
                              onChange={(e) => {
                                handleRowChange(e, index, "ContactDetails");
                              }}
                              disabled={formDisabled}
                              type="date"
                            />
                            <Input
                              name="VisitBegin"
                              label={data.VisitBeginLabel["@label"]}
                              value={item.VisitBegin}
                              onChange={(e) => {
                                handleRowChange(e, index, "ContactDetails");
                              }}
                              disabled={formDisabled}
                            />
                            <Input
                              name="VisitEnd"
                              label={data.VisitEndLabel["@label"]}
                              value={item.VisitEnd}
                              onChange={(e) => {
                                handleRowChange(e, index, "ContactDetails");
                              }}
                              disabled={formDisabled}
                            />
                            <Input
                              name="VisitParties"
                              label={data.VisitPartiesLabel["@label"]}
                              value={item.VisitParties}
                              onChange={(e) => {
                                handleRowChange(e, index, "ContactDetails");
                              }}
                              disabled={formDisabled}
                            />
                          </div>
                        )
                      )
                    : null}
                </div>
                {errors.ContactDetails !== undefined ? (
                  <div className="row">
                    <div className="col">
                      <div className="alert alert-danger">
                        {errors.ContactDetails}
                      </div>
                    </div>
                  </div>
                ) : null}
              </FieldTable>
              <div className="fw-bold">{data.WellbeingLabel["@label"]}</div>
              <Input
                name="CurrentBehaviors"
                label={data.CurrentBehaviors["@label"]}
                error={errors.CurrentBehaviors}
                value={getFieldValue(data.CurrentBehaviors)}
                onChange={handleChange}
                identical={compare("CurrentBehaviors")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="EmotionalFunctioning"
                label={data.EmotionalFunctioning["@label"]}
                error={errors.EmotionalFunctioning}
                value={getFieldValue(data.EmotionalFunctioning)}
                onChange={handleChange}
                identical={compare("EmotionalFunctioning")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="SocialFunctioning"
                label={data.SocialFunctioning["@label"]}
                error={errors.SocialFunctioning}
                value={getFieldValue(data.SocialFunctioning)}
                onChange={handleChange}
                identical={compare("SocialFunctioning")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="DailyActivities"
                label={data.DailyActivities["@label"]}
                error={errors.DailyActivities}
                value={getFieldValue(data.DailyActivities)}
                onChange={handleChange}
                identical={compare("DailyActivities")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="ChildAdjustment"
                label={data.ChildAdjustment["@label"]}
                error={errors.ChildAdjustment}
                value={getFieldValue(data.ChildAdjustment)}
                onChange={handleChange}
                identical={compare("ChildAdjustment")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <br />
              <div className="fw-bold">{data.ProgressLabel["@label"]}</div>
              <RadioButton
                name="PlanReviewed"
                label={data.PlanReviewed["@label"]}
                error={errors.PlanReviewed}
                value={getFieldValue(data.PlanReviewed)}
                options={JSON.parse(
                  data.PlanReviewed["@options"].replaceAll("'", '"')
                )}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <br />
              <div className="fw-bold">{data.FamilyUpdateLabel["@label"]}</div>
              <Input
                name="FamilyUpdate"
                label={data.FamilyUpdate["@label"]}
                error={errors.FamilyUpdate}
                value={getFieldValue(data.FamilyUpdate)}
                onChange={handleChange}
                identical={compare("FamilyUpdate")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <br />
              <div className="fw-bold">{data.PermanencyPlan["@label"]}</div>
              <Input
                name="PermanencyPlan"
                error={errors.PermanencyPlan}
                value={getFieldValue(data.PermanencyPlan)}
                onChange={handleChange}
                identical={compare("PermanencyPlan")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <br />
              <div className="fw-bold">{data.LifeBookUpdate["@label"]}</div>
              <RadioButton
                name="LifeBookUpdate"
                error={errors.LifeBookUpdate}
                value={getFieldValue(data.LifeBookUpdate)}
                options={JSON.parse(
                  data.LifeBookUpdate["@options"].replaceAll("'", '"')
                )}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <br />
              <div className="fw-bold">{data.VulnerabilityLabel["@label"]}</div>
              <Input
                name="ChildNeeds"
                label={data.ChildNeeds["@label"]}
                error={errors.ChildNeeds}
                value={getFieldValue(data.ChildNeeds)}
                onChange={handleChange}
                identical={compare("ChildNeeds")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="SafetyConcerns"
                label={data.SafetyConcerns["@label"]}
                error={errors.SafetyConcerns}
                value={getFieldValue(data.SafetyConcerns)}
                onChange={handleChange}
                identical={compare("SafetyConcerns")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="IRUpdate"
                label={data.IRUpdate["@label"]}
                error={errors.IRUpdate}
                value={getFieldValue(data.IRUpdate)}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <RadioButton
                name="IRReviewed"
                label={data.IRReviewed["@label"]}
                error={errors.IRReviewed}
                value={getFieldValue(data.IRReviewed)}
                options={JSON.parse(
                  data.IRReviewed["@options"].replaceAll("'", '"')
                )}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <RadioButton
                name="MedicationLogReviewed"
                label={data.MedicationLogReviewed["@label"]}
                error={errors.MedicationLogReviewed}
                value={getFieldValue(data.MedicationLogReviewed)}
                options={JSON.parse(
                  data.MedicationLogReviewed["@options"].replaceAll("'", '"')
                )}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <br />
              <div className="fw-bold">{data.AppointmentLabel["@label"]}</div>
              <RadioButton
                name="MonthlyApptReviewed"
                label={data.MonthlyApptReviewed["@label"]}
                error={errors.MonthlyApptReviewed}
                value={getFieldValue(data.MonthlyApptReviewed)}
                options={JSON.parse(
                  data.MonthlyApptReviewed["@options"].replaceAll("'", '"')
                )}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <RadioButton
                name="MonthlyMedicalApptReviewed"
                label={data.MonthlyMedicalApptReviewed["@label"]}
                error={errors.MonthlyMedicalApptReviewed}
                value={getFieldValue(data.MonthlyMedicalApptReviewed)}
                options={JSON.parse(
                  data.MonthlyMedicalApptReviewed["@options"].replaceAll(
                    "'",
                    '"'
                  )
                )}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <br />
              <div className="fw-bold">{data.ChangesLabel["@label"]}</div>
              <Checkbox
                name="ChangesMarital"
                label={data.ChangesMarital["@label"]}
                checked={getFieldValue(data.ChangesMarital) === "true"}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
                divClassName="ms-3"
              />
              <Checkbox
                name="ChangesHealth"
                label={data.ChangesHealth["@label"]}
                checked={getFieldValue(data.ChangesHealth) === "true"}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
                divClassName="ms-3"
              />
              <Checkbox
                name="ChangesPlacement"
                label={data.ChangesPlacement["@label"]}
                checked={getFieldValue(data.ChangesPlacement) === "true"}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
                divClassName="ms-3"
              />
              <Checkbox
                name="ChangesBirth"
                label={data.ChangesBirth["@label"]}
                checked={getFieldValue(data.ChangesBirth) === "true"}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
                divClassName="ms-3"
              />
              <Checkbox
                name="ChangesDeath"
                label={data.ChangesDeath["@label"]}
                checked={getFieldValue(data.ChangesDeath) === "true"}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
                divClassName="ms-3"
              />
              <Checkbox
                name="ChangesCrime"
                label={data.ChangesCrime["@label"]}
                checked={getFieldValue(data.ChangesCrime) === "true"}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
                divClassName="ms-3"
              />
              <Checkbox
                name="ChangesMembers"
                label={data.ChangesMembers["@label"]}
                checked={getFieldValue(data.ChangesMembers) === "true"}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
                divClassName="ms-3"
              />
              <Checkbox
                name="ChangesRelocation"
                label={data.ChangesRelocation["@label"]}
                checked={getFieldValue(data.ChangesRelocation) === "true"}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
                divClassName="ms-3"
              />
              <Checkbox
                name="ChangesActivities"
                label={data.ChangesActivities["@label"]}
                checked={getFieldValue(data.ChangesActivities) === "true"}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
                divClassName="ms-3"
              />
              <Checkbox
                name="ChangesEmployment"
                label={data.ChangesEmployment["@label"]}
                checked={getFieldValue(data.ChangesEmployment) === "true"}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
                divClassName="ms-3"
              />
              {checkChanges(data) ? (
                <Input
                  name="ChangesDetails"
                  label={data.ChangesDetails["@label"]}
                  error={errors.ChangesDetails}
                  value={getFieldValue(data.ChangesDetails)}
                  onChange={handleChange}
                  identical={compare("ChangesDetails")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              ) : null}
              <Input
                name="FamilyAdjustment"
                label={data.FamilyAdjustment["@label"]}
                error={errors.FamilyAdjustment}
                value={getFieldValue(data.FamilyAdjustment)}
                onChange={handleChange}
                identical={compare("FamilyAdjustment")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <br />
              <RadioButton
                name="ITPReviewed"
                label={data.ITPReviewed["@label"]}
                error={errors.ITPReviewed}
                value={getFieldValue(data.ITPReviewed)}
                options={JSON.parse(
                  data.ITPReviewed["@options"].replaceAll("'", '"')
                )}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <br />
              <div className="fw-bold">{data.FamilyNeedsLabel["@label"]}</div>
              <Input
                name="FamilyNeeds"
                label={data.FamilyNeeds["@label"]}
                error={errors.FamilyNeeds}
                value={getFieldValue(data.FamilyNeeds)}
                onChange={handleChange}
                identical={compare("FamilyNeeds")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <br />
              <Input
                name="SupportProvided"
                label={data.SupportProvided["@label"]}
                error={errors.SupportProvided}
                value={getFieldValue(data.SupportProvided)}
                onChange={handleChange}
                identical={compare("SupportProvided")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <br />
              <Input
                name="EmergingIssues"
                label={data.EmergingIssues["@label"]}
                error={errors.EmergingIssues}
                value={getFieldValue(data.EmergingIssues)}
                onChange={handleChange}
                identical={compare("EmergingIssues")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <br />
              <Input
                name="UpcomingEvents"
                label={data.UpcomingEvents["@label"]}
                error={errors.UpcomingEvents}
                value={getFieldValue(data.UpcomingEvents)}
                onChange={handleChange}
                identical={compare("UpcomingEvents")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
            </>
          )}
          <div>
            <div
              className="btn btn-primary m-2 d-print-none"
              onClick={() => {
                handleSubmit(null, null, null, null);
              }}
            >
              Save
            </div>
            <div className="mt-2 pt-2 border-top">
              Please be reminded that you are welcome to provide information and
              feedback at any time and you are always welcome to join us at
              meetings regarding any SAFY youth on your caseload.
            </div>
            <Signature
              canvasWidth="450"
              canvasHeight="200"
              modalTitle="Report Signatures"
              careTeams={careTeams}
              signatures={signatures}
              onSign={saveSignature}
              clientData={clientData.client}
              showModal={showSignatureModal}
              onHide={() => {
                setShowSignatureModal(false);
              }}
              finalSignType={helperConfiguration.finalSignType}
              initialSignatureType={helperConfiguration.signatureType}
            />
            <Comment
              showModal={showComment}
              commentToEdit={commentToEdit}
              onHide={() => {
                setShowComment(false);
                setCommentToEdit(undefined);
              }}
              onSave={onCommentSave}
              fields={commentFields()}
            />
            {showShare ? (
              <ShareModal
                showShare={showShare}
                onHide={() => setShowShare(false)}
                onSend={handleShareSend}
                submissionId={submissionid}
              />
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

export default OHTFCMonthlyVisitationMarionv2;
